
import React, { useState, useRef } from 'react';
import { 
  Send, 
  Paperclip, 
  Mic, 
  MicOff, 
  X, 
  Image as ImageIcon, 
  FileText, 
  Video,
  Smile
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from '@/hooks/use-toast';
import { MessageType } from '@/types/chat';

interface MessageInputProps {
  onSendMessage: (text: string, type: MessageType, file?: File) => void;
}

const MessageInput: React.FC<MessageInputProps> = ({ onSendMessage }) => {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const { toast } = useToast();
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  const handleSendMessage = () => {
    if (!message.trim()) return;
    
    onSendMessage(message, 'text');
    setMessage('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleAttachmentClick = (type: 'image' | 'document' | 'video') => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = 
        type === 'image' ? 'image/png, image/jpeg, image/gif' : 
        type === 'video' ? 'video/mp4, video/quicktime, video/webm' :
        '.pdf, .doc, .docx, .txt';
      
      fileInputRef.current.setAttribute('data-type', type);
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const fileType = e.target.getAttribute('data-type') as 'image' | 'document' | 'video';
    const messageType: MessageType = 
      fileType === 'image' ? 'image' : 
      fileType === 'video' ? 'video' : 'document';
    
    onSendMessage('', messageType, file);
    
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      
      recorder.ondataavailable = (e) => {
        chunksRef.current.push(e.data);
      };
      
      recorder.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/mp3' });
        const file = new File([audioBlob], `Voice_Message_${Date.now()}.mp3`, { type: 'audio/mp3' });
        
        onSendMessage('Voice message', 'voice', file);
        
        // Stop all tracks in the stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      recorder.start();
      setIsRecording(true);
      
      // Start recording timer
      let time = 0;
      timerRef.current = window.setInterval(() => {
        time += 1;
        setRecordingTime(time);
      }, 1000);
      
    } catch (err) {
      console.error('Error starting recording:', err);
      toast({
        title: "Recording Error",
        description: "Could not access microphone. Please check permissions.",
        variant: "destructive"
      });
    }
  };
  
  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    setIsRecording(false);
    setRecordingTime(0);
  };

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div>
      <input 
        type="file" 
        ref={fileInputRef} 
        className="hidden" 
        onChange={handleFileUpload}
      />
      
      {isRecording && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md p-3 mb-3 flex items-center justify-between">
          <div className="flex items-center">
            <div className="animate-pulse h-3 w-3 bg-red-500 rounded-full mr-3"></div>
            <span className="text-red-700 dark:text-red-400 font-medium">
              Recording {formatRecordingTime(recordingTime)}
            </span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={stopRecording} 
            className="text-red-700 dark:text-red-400"
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
        </div>
      )}
      
      <div className="flex gap-2 items-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" disabled={isRecording}>
              <Paperclip className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => handleAttachmentClick('image')}>
              <ImageIcon className="h-4 w-4 mr-2" />
              <span>Image</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleAttachmentClick('document')}>
              <FileText className="h-4 w-4 mr-2" />
              <span>Document</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleAttachmentClick('video')}>
              <Video className="h-4 w-4 mr-2" />
              <span>Video</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        <Button 
          variant="outline" 
          size="icon" 
          onClick={isRecording ? stopRecording : startRecording}
          className={isRecording ? "bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400" : ""}
        >
          {isRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </Button>
        
        <Button
          variant="outline"
          size="icon"
          disabled={isRecording}
          className="hidden sm:flex"
          onClick={() => toast({
            title: "Coming Soon",
            description: "Emoji picker will be available in a future update.",
          })}
        >
          <Smile className="h-4 w-4" />
        </Button>
        
        <Input 
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type your message..."
          className="flex-1"
          onKeyDown={handleKeyDown}
          disabled={isRecording}
        />
        
        <Button 
          onClick={handleSendMessage}
          disabled={!message.trim() || isRecording}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default MessageInput;
